import { SupportUserAvailabilityOverride } from "@redotech/crdb-enums/support-v2/support-user-availability-override";
import { isFieldBinding } from "@redotech/effect-api-shared/default-rules/field-binding";
import { useHandler } from "@redotech/react-util/hook";
import { TimeUnit } from "@redotech/redo-flows-common/rules-v3/types";
import {
  AssignV3Strategy,
  SetStatusActionStatus,
} from "@redotech/redo-flows-common/rules/actions";
import {
  AssignmentTypeOption,
  addTagActionSchema,
  assignActionSchema,
  assignOldestTicketActionSchema,
  assignV3ActionSchema,
  autoReplyActionSchema,
  handoffAiActionSchema,
  hideCommentActionSchema,
  likeCommentActionSchema,
  mergeSupportTicketActionSchema,
  removeTagActionSchema,
  runCustomCodeActionSchema,
  setPriorityActionSchema,
  setStatusActionSchema,
  setStatusV3ActionSchema,
  setUserAvailabilityActionSchema,
  splitSupportTicketActionSchema,
  startAiAgentExecutionActionSchema,
  stopTopicContinuationActionSchema,
  type RuleAction,
  type RuleActionsStep,
} from "@redotech/redo-flows-common/rules/schemas";
import {
  RULE_V3_ACTION_LABELS,
  RULE_V3_ACTION_TYPES,
  RuleActionType,
  RuleSubjectKind,
  RuleTriggerType,
  subjectKindForAction,
  subjectKindForTrigger,
} from "@redotech/redo-flows-common/rules/types";
import { StepType } from "@redotech/redo-flows-core-schema-common/common";
import { t, useTranslation } from "@redotech/redo-i18n";
import PlusIcon from "@redotech/redo-icons/plus.svg";
import TrashIcon from "@redotech/redo-icons/trash-03.svg";
import XIcon from "@redotech/redo-icons/x_filled.svg";
import { useDefaultRuleEditContext } from "@redotech/redo-merchant-support-app/setting/rules/default-rule-edit-context";
import { AddTagAction } from "@redotech/redo-merchant-support-app/setting/rules/flow-editor/actions/add-tag-action";
import { AssignAction } from "@redotech/redo-merchant-support-app/setting/rules/flow-editor/actions/assign-action";
import { AssignV3Action } from "@redotech/redo-merchant-support-app/setting/rules/flow-editor/actions/assign-v3-action";
import { MergeSupportTicketAction } from "@redotech/redo-merchant-support-app/setting/rules/flow-editor/actions/merge-support-ticket-action";
import { RemoveTagAction } from "@redotech/redo-merchant-support-app/setting/rules/flow-editor/actions/remove-tag-action";
import { RunCustomCodeAction } from "@redotech/redo-merchant-support-app/setting/rules/flow-editor/actions/run-custom-code-action";
import { SetPriorityAction } from "@redotech/redo-merchant-support-app/setting/rules/flow-editor/actions/set-priority-action";
import { SetStatusAction } from "@redotech/redo-merchant-support-app/setting/rules/flow-editor/actions/set-status-action";
import { SetStatusV3Action } from "@redotech/redo-merchant-support-app/setting/rules/flow-editor/actions/set-status-v3-action";
import { SetUserAvailabilityAction } from "@redotech/redo-merchant-support-app/setting/rules/flow-editor/actions/set-user-availability-action";
import { SplitSupportTicketAction } from "@redotech/redo-merchant-support-app/setting/rules/flow-editor/actions/split-support-ticket-action";
import { StopTopicContinuationAction } from "@redotech/redo-merchant-support-app/setting/rules/flow-editor/actions/stop-topic-continuation-action";
import { AutoReplyActionSwitch } from "@redotech/redo-merchant-support-app/v2/components/rules/auto-reply-action-switch";
import {
  CsatSurveyMethod,
  SurveySendTimeOptions,
} from "@redotech/redo-support-settings-common/csat-survey";
import { RedoButton } from "@redotech/redo-web/arbiter-components/buttons/redo-button";
import { Divider } from "@redotech/redo-web/arbiter-components/divider/divider";
import { RedoListItem } from "@redotech/redo-web/arbiter-components/list/redo-list";
import { RedoSingleSelectDropdownInput } from "@redotech/redo-web/arbiter-components/select-dropdown/redo-single-select-dropdown-input";
import { Flex } from "@redotech/redo-web/flex";
import * as inspectorStyles from "@redotech/redo-web/inspector.module.css";
import { Text } from "@redotech/redo-web/text";
import { assertNever } from "@redotech/util/type";
import { produce } from "immer";
import { memo, useMemo } from "react";
import { useAvailableSteps } from "../hooks/use-available-steps";
import type {
  DeepPartial,
  FrontendStepType,
  PartialWithRequiredType,
  StepTypeDetailsProps,
} from "../index";

function v2ActionTypeOptions(): RedoListItem<RuleActionType>[] {
  return [
    {
      id: RuleActionType.SET_STATUS,
      type: "text",
      text: t("Set status"),
      value: RuleActionType.SET_STATUS,
    },
    {
      id: RuleActionType.ADD_TAG,
      type: "text",
      text: t("Add tag"),
      value: RuleActionType.ADD_TAG,
    },
    {
      id: RuleActionType.AUTO_REPLY,
      type: "text",
      text: t("Auto reply"),
      value: RuleActionType.AUTO_REPLY,
    },
    {
      id: RuleActionType.ASSIGN,
      type: "text",
      text: t("Assign to user"),
      value: RuleActionType.ASSIGN,
    },
    {
      id: RuleActionType.LIKE_COMMENT,
      type: "text",
      text: t("Like comment"),
      value: RuleActionType.LIKE_COMMENT,
    },
    {
      id: RuleActionType.HIDE_COMMENT,
      type: "text",
      text: t("Hide comment"),
      value: RuleActionType.HIDE_COMMENT,
    },
  ];
}

// V3 options are derived from the single source of truth in
// `@redotech/redo-flows-common/rules/types`. To add, remove, or
// rename a V3 action, edit `RULE_V3_ACTION_TYPES` / `RULE_V3_ACTION_LABELS`
// there — the dropdown picks it up automatically. Legacy `ASSIGN` is not
// in the V3 list; `ASSIGN_V3` takes its place and is labeled just "Assign".
// A user-subject rule has no ticket to act on and a ticket rule has no
// teammate, so each subject offers only the actions it can answer for.
const v3ActionTypeOptionsFor = (
  kind: RuleSubjectKind,
): RedoListItem<RuleActionType>[] =>
  RULE_V3_ACTION_TYPES.filter(
    (type) => subjectKindForAction(type) === kind,
  ).map((type) => ({
    id: type,
    type: "text" as const,
    text: RULE_V3_ACTION_LABELS[type],
    value: type,
  }));

const v3ActionTypeOptionsBySubject: Record<
  RuleSubjectKind,
  RedoListItem<RuleActionType>[]
> = {
  [RuleSubjectKind.TICKET]: v3ActionTypeOptionsFor(RuleSubjectKind.TICKET),
  [RuleSubjectKind.USER]: v3ActionTypeOptionsFor(RuleSubjectKind.USER),
};

/** The action a new step starts as, per subject. Exhaustive by construction. */
const DEFAULT_ACTION_BY_SUBJECT: Record<
  RuleSubjectKind,
  DeepPartial<RuleAction>
> = {
  [RuleSubjectKind.TICKET]: {
    actionType: RuleActionType.SET_STATUS,
    status: SetStatusActionStatus.CLOSED,
  },
  [RuleSubjectKind.USER]: {
    actionType: RuleActionType.SET_USER_AVAILABILITY,
    status: SupportUserAvailabilityOverride.Inactive,
  },
};

const ActionEditor = memo(function ActionEditor({
  action,
  index,
  onUpdate,
  onDelete,
  isV3,
  subjectKind,
}: {
  action: DeepPartial<RuleAction>;
  index: number;
  onUpdate: (updated: DeepPartial<RuleAction>) => void;
  onDelete: () => void;
  isV3: boolean;
  subjectKind: RuleSubjectKind;
}) {
  const { t } = useTranslation();
  // Default-rule edit mode: the template fixes the action structure
  // (type + slot in the flow) and declares which fields inside the
  // action are editable via `FieldBinding` markers. Structural controls
  // (type dropdown, delete) lock; the leaf editor receives an
  // `editableFields` set derived from the template.
  const editCtx = useDefaultRuleEditContext();
  const structureLocked = editCtx !== null;
  const templateAction =
    editCtx && action.actionType
      ? editCtx.findActionTemplate(action.actionType)
      : undefined;
  const editableFields = useMemo<ReadonlySet<string> | undefined>(() => {
    if (!editCtx) {
      return undefined;
    }
    const set = new Set<string>();
    if (templateAction) {
      for (const [key, value] of Object.entries(templateAction)) {
        if (isFieldBinding(value)) {
          set.add(key);
        }
      }
    }
    return set;
  }, [editCtx, templateAction]);
  const actionTypeOptions = isV3
    ? v3ActionTypeOptionsBySubject[subjectKind]
    : v2ActionTypeOptions();
  const ActionTypeInspector = useMemo(() => {
    if (!action.actionType) {
      return null;
    }
    const actionType: RuleActionType = action.actionType;

    switch (actionType) {
      case RuleActionType.SET_STATUS:
        return (
          <SetStatusAction
            action={setStatusActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.SET_STATUS_V3:
        return (
          <SetStatusV3Action
            action={setStatusV3ActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.SET_PRIORITY:
        return (
          <SetPriorityAction
            action={setPriorityActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.ADD_TAG:
        return (
          <AddTagAction
            action={addTagActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.REMOVE_TAG:
        return (
          <RemoveTagAction
            action={removeTagActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.AUTO_REPLY:
        return (
          <AutoReplyActionSwitch
            action={autoReplyActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.ASSIGN:
        return (
          <AssignAction
            action={assignActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.ASSIGN_V3:
        return (
          <AssignV3Action
            action={assignV3ActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.ASSIGN_OLDEST_TICKET:
        return (
          <AssignV3Action
            action={assignOldestTicketActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.LIKE_COMMENT:
        likeCommentActionSchema.parse(action);
        return null;
      case RuleActionType.HIDE_COMMENT:
        hideCommentActionSchema.parse(action);
        return null;
      case RuleActionType.START_AI_AGENT_EXECUTION:
        startAiAgentExecutionActionSchema.parse(action);
        return null;
      case RuleActionType.HANDOFF_AI:
        handoffAiActionSchema.parse(action);
        return null;
      case RuleActionType.GENERATE_CONVERSATION_INSIGHTS:
      case RuleActionType.GENERATE_SUGGESTED_MERCHANT_KNOWLEDGE:
      case RuleActionType.SEND_CSAT_SURVEY:
        // Not in RULE_V3_ACTION_TYPES, so never in the dropdown; cases exist
        // only to keep the switch exhaustive when such a rule is loaded.
        return null;
      case RuleActionType.RUN_CUSTOM_CODE:
        return (
          <RunCustomCodeAction
            action={runCustomCodeActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.MERGE_SUPPORT_TICKET:
        return (
          <MergeSupportTicketAction
            action={mergeSupportTicketActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.SPLIT_SUPPORT_TICKET:
        return (
          <SplitSupportTicketAction
            action={splitSupportTicketActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.STOP_TOPIC_CONTINUATION:
        return (
          <StopTopicContinuationAction
            action={stopTopicContinuationActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      case RuleActionType.SET_USER_AVAILABILITY:
        return (
          <SetUserAvailabilityAction
            action={setUserAvailabilityActionSchema.parse(action)}
            editableFields={editableFields}
            onChange={onUpdate}
          />
        );
      default:
        assertNever(actionType);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [action.actionType, action]);

  return (
    <Flex dir="column" gap="md">
      <Flex align="center" justify="space-between" w="full">
        <Text fontSize="sm" fontWeight="medium">
          {t("Action {{actionNumber}}", { actionNumber: index + 1 })}
        </Text>
        {!structureLocked && (
          <RedoButton
            aria-label={t("Delete")}
            hierarchy="tertiary"
            IconLeading={TrashIcon}
            onClick={onDelete}
            size="sm"
          />
        )}
      </Flex>
      <RedoSingleSelectDropdownInput<RuleActionType>
        disabled={structureLocked}
        fitToAnchor
        label={t("Action type")}
        options={actionTypeOptions}
        optionSelected={({ item }) => {
          const value = item.value;

          let newAction: DeepPartial<RuleAction>;

          switch (value) {
            case RuleActionType.SET_STATUS:
              newAction = {
                actionType: RuleActionType.SET_STATUS,
                status: SetStatusActionStatus.CLOSED,
              };
              break;
            case RuleActionType.SET_STATUS_V3:
              newAction = {
                actionType: RuleActionType.SET_STATUS_V3,
                status: "CLOSED",
              };
              break;
            case RuleActionType.SET_PRIORITY:
              newAction = {
                actionType: RuleActionType.SET_PRIORITY,
                priority: "URGENT",
              };
              break;
            case RuleActionType.ADD_TAG:
              newAction = { actionType: RuleActionType.ADD_TAG, tag: [] };
              break;
            case RuleActionType.REMOVE_TAG:
              newAction = { actionType: RuleActionType.REMOVE_TAG, tag: [] };
              break;
            case RuleActionType.AUTO_REPLY:
              newAction = {
                actionType: RuleActionType.AUTO_REPLY,
                textContent: "",
                htmlContent: "",
              };
              break;
            case RuleActionType.ASSIGN:
              newAction = {
                actionType: RuleActionType.ASSIGN,
                user: { id: "", name: "" },
              };
              break;
            case RuleActionType.ASSIGN_V3:
              newAction = {
                actionType: RuleActionType.ASSIGN_V3,
                mode: { _tag: "specific_users", mongoUserIds: [] },
                strategy: AssignV3Strategy.ROUND_ROBIN,
                maxTicketsPerUser: null,
                currentAssignmentTypes: [AssignmentTypeOption.NO_ASSIGNEE],
              };
              break;
            case RuleActionType.ASSIGN_OLDEST_TICKET:
              newAction = {
                actionType: RuleActionType.ASSIGN_OLDEST_TICKET,
                mode: { _tag: "specific_users", mongoUserIds: [] },
                strategy: AssignV3Strategy.ROUND_ROBIN,
                maxTicketsPerUser: null,
              };
              break;
            case RuleActionType.LIKE_COMMENT:
              newAction = { actionType: RuleActionType.LIKE_COMMENT };
              break;
            case RuleActionType.HIDE_COMMENT:
              newAction = { actionType: RuleActionType.HIDE_COMMENT };
              break;
            case RuleActionType.START_AI_AGENT_EXECUTION:
              newAction = {
                actionType: RuleActionType.START_AI_AGENT_EXECUTION,
              };
              break;
            case RuleActionType.HANDOFF_AI:
              newAction = { actionType: RuleActionType.HANDOFF_AI };
              break;
            case RuleActionType.GENERATE_CONVERSATION_INSIGHTS:
              // Unreachable: this action is hidden from the dropdown via
              // `RULE_V3_ACTION_TYPES` / `v2ActionTypeOptions()`. Case exists
              // purely for exhaustiveness.
              newAction = {
                actionType: RuleActionType.GENERATE_CONVERSATION_INSIGHTS,
              };
              break;
            case RuleActionType.GENERATE_SUGGESTED_MERCHANT_KNOWLEDGE:
              // Unreachable: backend-only action hidden from the dropdown.
              newAction = {
                actionType:
                  RuleActionType.GENERATE_SUGGESTED_MERCHANT_KNOWLEDGE,
              };
              break;
            case RuleActionType.SEND_CSAT_SURVEY:
              // Unreachable: backend-only action hidden from the dropdown.
              // Case exists purely for exhaustiveness.
              newAction = {
                actionType: RuleActionType.SEND_CSAT_SURVEY,
                surveySendTime: SurveySendTimeOptions.ONE_HOUR,
                surveyMethods: [CsatSurveyMethod.EMAIL],
                allowRemarks: true,
                headerImage: "",
                surveyMessage: "",
              };
              break;
            case RuleActionType.MERGE_SUPPORT_TICKET:
              newAction = {
                actionType: RuleActionType.MERGE_SUPPORT_TICKET,
                allowedStatuses: ["OPEN", "IN_PROGRESS"],
                maxAgeSinceLastMessage: { value: 7, unit: TimeUnit.DAYS },
              };
              break;
            case RuleActionType.SPLIT_SUPPORT_TICKET:
              // Unreachable: removed from RULE_V3_ACTION_TYPES so it never
              // appears in the dropdown. Case exists purely for exhaustiveness.
              newAction = {
                actionType: RuleActionType.SPLIT_SUPPORT_TICKET,
                routeFutureMessagesToNewTicket: true,
              };
              break;
            case RuleActionType.STOP_TOPIC_CONTINUATION:
              newAction = {
                actionType: RuleActionType.STOP_TOPIC_CONTINUATION,
                channels: [],
              };
              break;
            case RuleActionType.RUN_CUSTOM_CODE:
              // Starts empty; the editor pane sets codeArtifactId on save.
              newAction = {
                actionType: RuleActionType.RUN_CUSTOM_CODE,
                codeArtifactId: "",
              };
              break;
            case RuleActionType.SET_USER_AVAILABILITY:
              newAction = {
                actionType: RuleActionType.SET_USER_AVAILABILITY,
                status: SupportUserAvailabilityOverride.Inactive,
              };
              break;
            default:
              assertNever(value);
          }

          onUpdate(newAction);
        }}
        placeholder={t("Select action type")}
        selectedItem={
          action.actionType
            ? actionTypeOptions.find((opt) => opt.value === action.actionType)
            : undefined
        }
      />
      {action.actionType && ActionTypeInspector && ActionTypeInspector}
      {action.actionType === RuleActionType.LIKE_COMMENT && (
        <Text fontSize="xs" textColor="secondary">
          {t("Only supported for {{platformName}} comment conversations.", {
            platformName: "Facebook",
          })}
        </Text>
      )}
    </Flex>
  );
});

const RuleActionsInspector = memo(function RuleActionsInspector({
  step,
  steps,
  setStep,
  onClose,
  setHoveredNodeId,
  automation,
}: StepTypeDetailsProps<PartialWithRequiredType<RuleActionsStep>>) {
  const { t } = useTranslation();
  // Structural controls (add-action, next-step routing) lock when the
  // step is part of a default-rule template.
  const structureLocked = useDefaultRuleEditContext() !== null;
  // This inspector is shared between the V2 (RULE_ACTIONS) and V3
  // (RULE_ACTIONS_V3) step types in step-types.ts. The step schemas are
  // structurally identical, so the cast is safe — we use it only to decide
  // which subset of actions to offer in the dropdown.
  const isV3 = (step.type as StepType) === StepType.RULE_ACTIONS_V3;

  const triggerStep = automation?.steps?.find(
    (s) => s.type === StepType.TRIGGER,
  );
  const triggerType = (triggerStep as { key?: string } | undefined)?.key as
    | RuleTriggerType
    | undefined;
  // The trigger key is untyped step data; an unset or unknown one falls back to
  // the ticket actions, which all but one trigger is about.
  const subjectKind =
    triggerType !== undefined &&
    Object.values(RuleTriggerType).includes(triggerType)
      ? subjectKindForTrigger(triggerType)
      : RuleSubjectKind.TICKET;
  const handleAddAction = useHandler(() => {
    const newActions = [
      ...(step.actions || []),
      DEFAULT_ACTION_BY_SUBJECT[subjectKind],
    ];
    setStep({
      ...step,
      actions: newActions as DeepPartial<RuleActionsStep["actions"]>,
    });
  });

  const handleDeleteAction = useHandler((index: number) => {
    const newActions = (step.actions || []).filter((_, i) => i !== index);
    setStep({
      ...step,
      actions: newActions as DeepPartial<RuleActionsStep["actions"]>,
    });
  });

  const handleUpdateAction = useHandler(
    (index: number, updated: DeepPartial<RuleAction>) => {
      const newActions = [...(step.actions || [])];
      newActions[index] = updated;
      setStep({
        ...step,
        actions: newActions as DeepPartial<RuleActionsStep["actions"]>,
      });
    },
  );

  const nextStep = step.nextId
    ? steps.find((s) => s.id === step.nextId)
    : undefined;

  const { availableSteps, createConnectionHandler, onItemHover } =
    useAvailableSteps(step, steps, setStep, setHoveredNodeId);

  const handleNextStepSelected = createConnectionHandler(0);

  return (
    <Flex dir="column" gap="xl" justify="flex-start">
      <Flex justify="space-between" w="full">
        <Text fontSize="lg" fontWeight="semibold">
          {t("Rule actions")}
        </Text>
        {onClose && (
          <RedoButton
            aria-label={t("Close")}
            hierarchy="tertiary"
            IconLeading={XIcon}
            onClick={onClose}
            size="sm"
          />
        )}
      </Flex>

      <Text fontSize="sm" textColor="secondary">
        {t(
          "Add actions to perform when conditions match. Actions are executed in order.",
        )}
      </Text>

      <Flex dir="column" gap="lg">
        {step.actions?.map((action, index) => (
          <>
            {index > 0 && <Divider />}
            <ActionEditor
              action={action}
              index={index}
              isV3={isV3}
              key={index}
              onDelete={() => handleDeleteAction(index)}
              onUpdate={(updated) => handleUpdateAction(index, updated)}
              subjectKind={subjectKind}
            />
          </>
        ))}

        {(!step.actions || step.actions.length === 0) && (
          <Flex className={inspectorStyles.emptyState} dir="column" gap="sm">
            <Text fontSize="sm" textColor="secondary">
              {t("No actions added yet. At least one action is required.")}
            </Text>
          </Flex>
        )}

        {!structureLocked && (
          <RedoButton
            hierarchy="secondary"
            IconLeading={PlusIcon}
            onClick={handleAddAction}
            size="md"
          >
            {t("Add action")}
          </RedoButton>
        )}
      </Flex>

      <Divider />

      <Flex dir="column" gap="lg">
        <Text fontSize="md" fontWeight="semibold">
          {t("Next Step")}
        </Text>

        <Flex dir="column" gap="md">
          <RedoSingleSelectDropdownInput<string>
            disabled={structureLocked}
            fitToAnchor
            label={t("After actions complete")}
            onOptionMouseEnter={(item) => onItemHover(item.value)}
            onOptionMouseLeave={() => onItemHover(null)}
            options={availableSteps}
            optionSelected={({ item }) => {
              if (structureLocked) {
                return;
              }
              handleNextStepSelected(item.value);
            }}
            placeholder={t("Select a step or choose Unassign")}
            selectedItem={
              nextStep
                ? availableSteps.find((opt) => opt.value === nextStep.id)
                : undefined
            }
          />
        </Flex>
      </Flex>
    </Flex>
  );
});

export const ruleActionsStepType: FrontendStepType<
  PartialWithRequiredType<RuleActionsStep>
> = {
  valid: (state) => {
    if (!state.actions || state.actions.length === 0) {
      return false;
    }

    // Check if any auto-reply action has empty text
    for (const action of state.actions) {
      if (action.actionType === RuleActionType.AUTO_REPLY) {
        const textContent = action.textContent?.trim() || "";
        if (textContent === "" || textContent === "\n") {
          return false;
        }
      }

      // Check if any add-tag or remove-tag action has no tags
      if (
        (action.actionType === RuleActionType.ADD_TAG ||
          action.actionType === RuleActionType.REMOVE_TAG) &&
        (!action.tag || action.tag.length === 0)
      ) {
        return false;
      }

      // Validate MERGE_SUPPORT_TICKET: at least one allowed status and a
      // positive duration are required — both are user-configured, no defaults.
      if (action.actionType === RuleActionType.MERGE_SUPPORT_TICKET) {
        if (!action.allowedStatuses || action.allowedStatuses.length === 0) {
          return false;
        }
        const age = action.maxAgeSinceLastMessage;
        if (
          !age ||
          typeof age.value !== "number" ||
          !Number.isFinite(age.value) ||
          age.value < 1 ||
          !age.unit
        ) {
          return false;
        }
      }

      if (
        action.actionType === RuleActionType.RUN_CUSTOM_CODE &&
        !action.codeArtifactId
      ) {
        return false;
      }

      // Validate ASSIGN_V3 / ASSIGN_OLDEST_TICKET: pool must not be empty
      if (
        action.actionType === RuleActionType.ASSIGN_V3 ||
        action.actionType === RuleActionType.ASSIGN_OLDEST_TICKET
      ) {
        const mode = action.mode;
        if (!mode || !mode._tag) {
          return false;
        }
        if (mode._tag === "specific_users") {
          if (!mode.mongoUserIds || mode.mongoUserIds.length === 0) {
            return false;
          }
        } else if (mode._tag === "user_group" && !mode.userGroupId) {
          return false;
        } else if (
          mode._tag === "actor" &&
          (!mode.assigneeTypes || mode.assigneeTypes.length === 0)
        ) {
          return false;
        }
      }
    }

    return true;
  },
  type: "custom",
  customInspector: RuleActionsInspector,
  updateConnection: (state, handleIndex, targetStepId) => {
    return produce(state, (draft) => {
      if (handleIndex === 0) {
        draft.nextId = targetStepId;
      } else {
        throw new Error(
          `Invalid handle index ${handleIndex} for Rule Actions step`,
        );
      }
    });
  },
  removeConnection: (state, targetStepId) => {
    return produce(state, (draft) => {
      if (draft.nextId === targetStepId) {
        draft.nextId = undefined;
      }
    });
  },
};
